import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { positionReferenceOptionsQuery } from '../workdayPosition/api';
import { fetchReferenceOptions } from './hireReferenceOptions';
import { toWorkdayOption } from '../workdayReferences';

// Workday's reference lists are cached server-side anyway, and they do not
// change while a recruiter works through a hire.
const HIRE_OPTIONS_STALE_TIME = 5 * 60 * 1000;

/**
 * The reference type a hire-form link points at.
 *
 * The hire form hands back a ready-made URL rather than a type, but it is the
 * same `position_reference_options` endpoint the position form calls — so
 * pulling the type out lets both read one cache entry instead of keying the
 * same list twice. Job profiles alone are ~1,750 rows, and a recruiter who
 * creates a position and then hires would otherwise fetch every list twice.
 */
const referenceTypeFromLink = (link: string): string | undefined => {
  try {
    return (
      new URL(link, 'http://localhost').searchParams.get('type') ?? undefined
    );
  } catch {
    return undefined;
  }
};

/**
 * Loader for the hire form's dropdowns, reading through the shared cache.
 *
 * `fetchQuery` rather than `useQuery` because the form asks imperatively, once
 * per referenced select; this is also the only dedupe layer, so several fields
 * pointing at one list share a single request.
 */
export const useHireReferenceOptions = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (link: string) => {
      const type = referenceTypeFromLink(link);
      // A link with no `type` is not the shared endpoint — follow it verbatim
      // under its own key rather than guessing.
      if (!type) {
        return queryClient.fetchQuery({
          queryKey: ['workdayHire', 'referenceOptions', link],
          queryFn: () => fetchReferenceOptions(link),
          staleTime: HIRE_OPTIONS_STALE_TIME,
        });
      }
      return (
        queryClient
          .fetchQuery(positionReferenceOptionsQuery(type))
          // Same mapper the position form uses, so a list looks identical in
          // both — underscores spaced out, a team's manager folded in.
          //
          .then((response: any) =>
            (response?.data?.results || []).map(toWorkdayOption)
          )
      );
    },
    [queryClient]
  );
};
